import { useEffect, useState } from "react";

// Third party
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { enqueueSnackbar, SnackbarKey } from "notistack";

// Materials
import Grid from "@mui/material/Grid";

// Helpers
import { matchPositions } from "@/Common/helpers/Positions";

// Custom components
import { SnackbarsActions, SnackbarsContent } from "@/Layouts/components/Snackbars";
import { ReviewerCard } from "./ReviewerCard";
import SubAvatarCard from "../../SubAvatarCard";
import CommissionCycleClosingReviewersLoader from "./CommissionCycleClosingReviewersLoader";

// Hooks
import { useManagedPositions } from "@/Common/hooks/Positions";

// API
import { IGetWashoutReview } from "@/Washouts/types/PayrollClosing/queries";
import { IStatus } from "@/Common/api/types/CommissionCycle";
import { getWashoutApprovedByPosition } from "@/Washouts/api/PayrollClosing/queries";
import { getAllReviewersSystemSettings } from "@/Washouts/api/common/queries";

// Types
import {
	IReviewerIds,
	IWashoutApprovedByPositionItem,
	IReviewerStatus,
} from "@/Washouts/types/PayrollClosing/CommissionCycleClosingReviewers";

const getRegularStatus = (status: string, hasWashoutApproved: boolean): string => {
	switch (status) {
		case "APPROVED":
		case "POSTED_PAYROLL":
			return "Approved";
		case "REVIEWING":
			return hasWashoutApproved ? "In Progress" : "Pending Review";
		case "PENDING":
		default:
			return "";
	}
};

const mergeReviewers = (
	washoutApprovedByPosition: IWashoutApprovedByPositionItem[] = [],
	reviewers: IReviewerStatus[] = [],
	statuses: IStatus[] = []
): IReviewerStatus[] => {
	const onlyStatuses = statuses.filter((status) => !reviewers.some((reviewer) => reviewer.position === status.position));
	const mappedStatuses = onlyStatuses.map((status) => {
		const reviewer = washoutApprovedByPosition.find((position) => position.position === status.position);
		return {
			email: "Unknown",
			name: "No Reviewer Assigned",
			phone: "Unknown",
			picturePath: "Unknown",
			position: matchPositions(status.position),
			status: getRegularStatus(status.status, !!reviewer?.userIds.length),
			title: "Unknown Position",
		};
	});
	return [...reviewers, ...mappedStatuses];
};

const renderSnackbarsActions = (key: SnackbarKey) => {
	return <SnackbarsActions key={key.toString()} index={key} />;
};

type CommissionCycleClosingReviewersProps = {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	statuses: IStatus[];
};

export default function CommissionCycleClosingReviewers(props: Readonly<CommissionCycleClosingReviewersProps>) {
	const { locationId, commissionCycleId, payPeriodId, statuses } = props;

	const [reviewers, setReviewers] = useState<{ ids: IReviewerIds[]; data: IReviewerStatus[] }>();
	const [substituteReviewers, setSubstituteReviewers] = useState(new Map());

	const { data: currentListManagedPositions, isLoading: loadingPositions } = useManagedPositions({
		queryOpts: {
			staleTime: Infinity,
		},
	});

	const { data: washoutApprovedByPosition, isLoading: loadingWashoutApprovedByPosition } = useQuery({
		queryKey: ["washoutApprovedByPosition", locationId, payPeriodId],
		queryFn: async () => {
			const response = await getWashoutApprovedByPosition(
				locationId ?? "",
				payPeriodId,
				statuses.map((status) => status.position)
			);
			return response?.positions;
		},
		enabled: !!locationId && !!payPeriodId,
		staleTime: Infinity,
	});

	const { data: locationReviewers, isLoading: loadingLocationReviewers } = useQuery({
		queryKey: ["commissionCycleSectionReviewersSystemSettings", locationId],
		queryFn: async () => getAllReviewersSystemSettings(locationId ?? ""),
		enabled: !!locationId,
		staleTime: Infinity,
	});

	const {
		data: substituteReviewersSalesAssociateWashouts,
		isLoading: loadingSubstitutes,
		dataUpdatedAt,
	} = useQuery({
		queryKey: ["commissionCycleSectionSubstituteReviewersSalesAssociateWashouts", locationId, commissionCycleId],
		queryFn: async (): Promise<IGetWashoutReview[] | null> => {
			try {
				// TODO: Implement the right API call
				return [] as IGetWashoutReview[];
			} catch {
				enqueueSnackbar(<SnackbarsContent message={"An error occurred retrieving the Sales Associate substitute reviewers."} critical={true} />, {
					variant: "error",
					preventDuplicate: true,
					action: renderSnackbarsActions,
				});

				return null;
			}
		},
		staleTime: Infinity,
		enabled: !!locationId && !!commissionCycleId,
	});

	const allReviewers: IReviewerStatus[] = mergeReviewers(washoutApprovedByPosition, reviewers?.data, statuses);

	useEffect(() => {
		if (!locationReviewers?.washoutReviewers.length || !currentListManagedPositions?.length || !commissionCycleId) {
			return;
		}

		try {
			const object: IReviewerStatus[] = [];
			const ids: IReviewerIds[] = [];

			const washoutsReviewers = locationReviewers.washoutReviewers.filter((reviewer) => reviewer.reviewerId);
			washoutsReviewers?.forEach((element) => {
				const reviewStatus = statuses?.find((status) => status.position === element.position);

				if (reviewStatus && element.reviewerId._id) {
					const userPhone = element?.reviewerId?.communications?.find((comm) => {
						return comm.communicationType === "PHONE" && comm.usage === "BUSINESS";
					});

					const userEmail = element?.reviewerId?.communications?.find((comm) => {
						return comm.communicationType === "EMAIL" && comm.usage === "BUSINESS";
					});

					const found = currentListManagedPositions?.includes(element.position);

					if (found) {
						const position = washoutApprovedByPosition?.find((item) => item.position === element.position);
						object.push({
							picturePath: element.reviewerId.presignedProfilePicture ?? "Holder",
							name: element.reviewerId.fullName ?? "",
							title: element.reviewerId.position ?? "",
							email: userEmail?.value ?? "Unknown",
							phone: userPhone?.value ?? "Unknown",
							status: getRegularStatus(reviewStatus.status, !!position?.userIds.length),
							position: element.position,
						});
						ids.push({ id: element.reviewerId._id ?? "", position: element.position });
					}
				}
			});

			setReviewers({
				ids,
				data: object,
			});
		} catch {
			enqueueSnackbar(<SnackbarsContent message={"An error occurred retrieving the reviewers."} critical={true} />, {
				variant: "error",
				preventDuplicate: true,
				action: renderSnackbarsActions,
			});
		}
	}, [currentListManagedPositions, enqueueSnackbar, washoutApprovedByPosition, locationReviewers, props]);

	useEffect(() => {
		const substitutes = new Map();

		const mainReviewer = reviewers?.data?.find((entry) => entry.position === "Sales Associate")?.name;
		if (mainReviewer && substituteReviewersSalesAssociateWashouts?.length) {
			const approved = substituteReviewersSalesAssociateWashouts.filter(
				(entry) => entry?.review?.status === "APPROVED" && entry?.review?.statusSetBy?.fullName !== mainReviewer
			);

			const result = approved.filter(
				(entry, index, self) =>
					self.findIndex((value) => value.review?.statusSetBy?.fullName === entry.review?.statusSetBy?.fullName) === index
			);

			substitutes.set("Sales Associate", result);
		}

		setSubstituteReviewers(substitutes);
	}, [reviewers, substituteReviewersSalesAssociateWashouts, dataUpdatedAt]);

	if (loadingSubstitutes || loadingPositions || loadingWashoutApprovedByPosition || loadingLocationReviewers) {
		return <CommissionCycleClosingReviewersLoader cardsLength={allReviewers.length} />;
	}

	return (
		<Grid
			item
			xs={12}
			className={clsx("!my-9 !pt-1 !pb-5 pl-5 !pr-2 relative max-h-[480px] overflow-y-auto", {
				"!pr-4": (allReviewers.length ?? 0) > 9,
			})}
		>
			<Grid container className="flex-row" spacing={5.5}>
				{allReviewers.map((reviewer, idx) => {
					const substituteReviewer = substituteReviewers?.get(reviewer.position) ?? [];
					const key = `${reviewer.title}_${idx}`;
					return (
						<ReviewerCard reviewer={reviewer} key={key}>
							{substituteReviewer.length > 0 ? (
								<Grid item className="z-[100]">
									<SubAvatarCard title={"Substitute Reviewers"} content={substituteReviewers?.get(reviewer.position)} />
								</Grid>
							) : (
								""
							)}
						</ReviewerCard>
					);
				})}
			</Grid>
		</Grid>
	);
}
